import { isResource } from '@medplum/core'
import type {
  Attachment,
  Communication,
  Organization,
  Patient,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import { hasCodeableConcept } from '#src/codeableConcept.ts'
import { AI_THREAD_LANGFUSE_TRACE_ID_SYSTEM } from '#src/codeSystems.ts'
import { base64Decode, base64Encode } from '#src/encoding.ts'
import { COMMUNICATION_CATEGORIES } from '#src/models/communications/constants.ts'
import { ExtendedCommunication } from '#src/models/communications/ExtendedCommunication.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType } from '#src/utils.ts'

export class AIThread extends ExtendedCommunication {
  static allForContext(contextRef: Reference): QueryTypes {
    return {
      ...ExtendedCommunication.baseSearchQuery(),
      category: COMMUNICATION_CATEGORIES.aiThread,
      'part-of': contextRef,
      _sort: '-sent',
    }
  }

  static allForContextAndAuthor(
    contextRef: Reference,
    authorRef: Reference<Practitioner>
  ): QueryTypes {
    return {
      ...AIThread.allForContext(contextRef),
      sender: authorRef,
    }
  }

  static activeForContext(contextRef: Reference): QueryTypes {
    return {
      ...AIThread.allForContext(contextRef),
      status: 'in-progress',
    }
  }

  static activeForContextAndAuthor(
    contextRef: Reference,
    authorRef: Reference<Practitioner>
  ): QueryTypes {
    return {
      ...AIThread.allForContextAndAuthor(contextRef, authorRef),
      status: 'in-progress',
    }
  }

  constructor(data: {
    partOf: Reference[]
    sender?: Reference<Practitioner | Organization>
    subject?: Reference<Patient>
    status?: Communication['status']
  }) {
    super({ ...data, category: COMMUNICATION_CATEGORIES.aiThread })
  }

  get threadTitle(): string | undefined {
    return (
      this.resource.payload?.find((p) => p.contentAttachment)?.contentAttachment
        ?.title || undefined
    )
  }

  set threadTitle(title: string) {
    const attachment = this.resource.payload?.find((p) => p.contentAttachment)
    if (attachment?.contentAttachment) {
      attachment.contentAttachment.title = title
    } else {
      this.resource.payload = [
        ...(this.resource.payload ?? []),
        { contentAttachment: { title } },
      ]
    }
  }

  get langfuseTraceId(): string | undefined {
    return this.identifier(AI_THREAD_LANGFUSE_TRACE_ID_SYSTEM)?.value
  }

  set langfuseTraceId(traceId: string | undefined) {
    if (traceId) {
      this.setIdentifier(AI_THREAD_LANGFUSE_TRACE_ID_SYSTEM, traceId)
    } else {
      this.removeIdentifier(AI_THREAD_LANGFUSE_TRACE_ID_SYSTEM)
    }
  }

  get messagesJson(): string | undefined {
    const attachment = this.resource.payload?.find(
      (p) => p.contentAttachment?.contentType === 'application/json'
    )
    if (!attachment?.contentAttachment?.data) {
      return undefined
    }
    return base64Decode(attachment.contentAttachment.data)
  }

  set messagesJson(json: string) {
    const currentTitle = this.threadTitle
    const contentAttachment: Attachment = {
      contentType: 'application/json',
      data: base64Encode(json),
    }
    if (currentTitle) {
      contentAttachment.title = currentTitle
    }
    this.resource.payload = (this.resource.payload ?? [])
      .filter((p) => p.contentAttachment?.contentType !== 'application/json')
      .concat({ contentAttachment })
  }

  // A resource the thread was carried to after it started. Kept out of
  // `partOf` so the resource's own thread lookups (auto-resume, history) never
  // pick the thread up.
  isAbout(reference: string): boolean {
    return (this.resource.about ?? []).some((r) => r.reference === reference)
  }

  addAbout(reference: string): void {
    if (!this.isAbout(reference)) {
      this.resource.about = [...(this.resource.about ?? []), { reference }]
    }
  }

  static isInstance(value: unknown): value is Communication {
    return (
      isResource(value) &&
      isOfType(value, 'Communication') &&
      hasCodeableConcept(value.category, COMMUNICATION_CATEGORIES.aiThread)
    )
  }
}
